import { useMemo, useState } from 'react'
import { Link, useParams, Navigate } from 'react-router-dom'
import { FaArrowLeft, FaArrowRight, FaYoutube, FaPlay } from 'react-icons/fa'
import { TbMicrophone2 } from 'react-icons/tb'
import { FALLBACK_OFFERS, formatSessionDate, formatDuration, type LiveOffers } from '../config/cta'
import { ContentColumn } from '../components/layout/ContentColumn/ContentColumn'
import InlineSubscribe from '../components/content/InlineSubscribe/InlineSubscribe'
import { PODCAST_PITCH } from '../config/subscribePitch'

// ── Description parsing ──────────────────────────────────────────────────────
// Episode descriptions are stored as a single YouTube-style text blob. We split
// it into structured sections by known headings so the page can render an intro,
// a takeaways list, clickable chapters, a "go deeper" CTA, and links instead of
// one run-on paragraph. Everything is optional and degrades gracefully: an
// episode with no headings just yields an intro.

type Chapter = { seconds: number; time: string; label: string }
type EpLink = { label: string; url: string }
type ParsedDescription = {
  intro: string
  takeaways: string[]
  chapters: Chapter[]
  goDeeper: string
  links: EpLink[]
}

const HEADINGS: { key: keyof Omit<ParsedDescription, 'intro'>; match: RegExp }[] = [
  { key: 'takeaways', match: /^(what you(?:'ll| will) take away|in this episode)\s*:?\s*$/i },
  { key: 'chapters', match: /^chapters\s*:?\s*$/i },
  { key: 'goDeeper', match: /^go deeper\s*:?\s*$/i },
  { key: 'links', match: /^links\s*:?\s*$/i },
]

function toSeconds(t: string): number {
  const parts = t.split(':').map(Number)
  return parts.length === 3 ? parts[0] * 3600 + parts[1] * 60 + parts[2] : parts[0] * 60 + parts[1]
}

function parseDescription(raw?: string): ParsedDescription {
  const out: ParsedDescription = { intro: '', takeaways: [], chapters: [], goDeeper: '', links: [] }
  if (!raw) return out
  const lines = raw.replace(/\r/g, '').split('\n')
  const introLines: string[] = []
  let current: keyof Omit<ParsedDescription, 'intro'> | null = null
  const buckets: Record<string, string[]> = { takeaways: [], chapters: [], goDeeper: [], links: [] }

  for (const line of lines) {
    const heading = HEADINGS.find((h) => h.match.test(line.trim()))
    if (heading) { current = heading.key; continue }
    if (current) buckets[current].push(line)
    else introLines.push(line)
  }

  out.intro = introLines.join('\n').trim()
  out.takeaways = buckets.takeaways
    .map((l) => l.replace(/^\s*[-•*]\s*/, '').trim())
    .filter(Boolean)
  out.chapters = buckets.chapters
    .map((l) => {
      const m = l.trim().match(/^(\d{1,2}:\d{2}(?::\d{2})?)\s+(.*)$/)
      return m ? { seconds: toSeconds(m[1]), time: m[1], label: m[2].trim() } : null
    })
    .filter((c): c is Chapter => !!c)
  out.goDeeper = buckets.goDeeper.join('\n').trim()
  out.links = buckets.links
    .map((l) => {
      const m = l.trim().match(/^(.*?):\s*(https?:\/\/\S+)\s*$/)
      if (m) return { label: m[1].trim(), url: m[2] }
      const bare = l.trim().match(/^(https?:\/\/\S+)$/)
      return bare ? { label: bare[1].replace(/^https?:\/\//, ''), url: bare[1] } : null
    })
    .filter((l): l is EpLink => !!l)
  return out
}

function PodcastEpisodePage({ episodes = [], offers = FALLBACK_OFFERS }: { episodes?: any[]; offers?: LiveOffers }) {
  const { slug } = useParams<{ slug: string }>()
  const [startAt, setStartAt] = useState<number | null>(null)

  const allEpisodes = episodes
  const episode = useMemo(
    () => (slug ? allEpisodes.find((e: any) => e.slug === slug) ?? null : null),
    [slug, allEpisodes],
  )
  const parsed = useMemo(() => parseDescription(episode?.description), [episode])

  if (!episode) return <Navigate to="/podcast" replace />

  const currentIndex = allEpisodes.findIndex((ep) => ep.slug === episode.slug)
  const newerEpisode = currentIndex > 0 ? allEpisodes[currentIndex - 1] : null
  const olderEpisode = currentIndex < allEpisodes.length - 1 ? allEpisodes[currentIndex + 1] : null

  const videoId: string | null =
    episode.youtubeId ||
    (episode.youtube?.includes('watch?v=')
      ? episode.youtube.split('watch?v=')[1]?.split('&')[0]
      : episode.youtube?.includes('youtu.be/')
        ? episode.youtube.split('youtu.be/')[1]?.split(/[?&]/)[0]
        : null)

  const embedUrl = videoId
    ? `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1${startAt != null ? `&start=${startAt}&autoplay=1` : ''}`
    : null

  const publishedDate = episode.publishedAt
    ? new Date(episode.publishedAt).toLocaleDateString('en-US', { timeZone: 'UTC', year: 'numeric', month: 'long', day: 'numeric' })
    : null

  const runtimeMin = parsed.chapters.length
    ? Math.ceil(parsed.chapters[parsed.chapters.length - 1].seconds / 60)
    : null

  // The "Go deeper" CTA points at whatever is live now, never at the Maven link
  // an episode shipped with: those name a specific cohort and die the day it
  // ends. Any Maven link in the description is still stripped out of the
  // resource links, so a finished cohort can't resurface there either.
  const otherLinks = parsed.links.filter((l) => !/maven\.com/.test(l.url))
  // A free session is the easier yes; the cohort carries the panel when nothing
  // is scheduled.
  const goDeeper = offers.lesson ?? offers.course

  const scriptParagraphs: string[] = episode.script
    ? episode.script
        .split('\n')
        .map((l: string) => l.trim())
        .filter(
          (l: string) =>
            l &&
            !l.startsWith('GAZAR BREAKPOINT') &&
            !l.startsWith('Published:') &&
            !l.startsWith('YouTube:') &&
            l !== '---',
        )
    : []

  const seek = (seconds: number) => setStartAt(seconds === 0 ? 0 : seconds)

  return (
    <div className="bg-brand-cream min-h-screen">
      {/* ── Hero ── */}
      <section className="bg-brand-navy text-white">
        <ContentColumn anchor="podcast-hero" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 md:pt-20 pb-28 md:pb-36">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 font-heading text-caption uppercase tracking-[0.18em] text-white/65">
              <li><Link to="/" className="text-white/65 hover:text-white transition-colors">Home</Link></li>
              <li aria-hidden="true">/</li>
              <li><Link to="/podcast" className="text-white/65 hover:text-white transition-colors">Podcast</Link></li>
              <li aria-hidden="true">/</li>
              <li className="text-white" aria-current="page">Ep. {episode.episode}</li>
            </ol>
          </nav>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mb-5 font-heading text-overline uppercase tracking-[0.16em]">
            <span className="inline-flex items-center gap-2 text-brand-red">
              <TbMicrophone2 className="text-sm" aria-hidden="true" />
              Gazar Breakpoint
            </span>
            <span className="text-white/25" aria-hidden="true">·</span>
            <span className="text-white/55">Episode {episode.episode}</span>
            {publishedDate && <><span className="text-white/25" aria-hidden="true">·</span><span className="text-white/55">{publishedDate}</span></>}
            {runtimeMin && <><span className="text-white/25" aria-hidden="true">·</span><span className="text-white/55">{runtimeMin} min</span></>}
          </div>

          <h1 className="font-sans text-[34px] sm:text-[44px] md:text-[52px] font-bold leading-[1.08] tracking-[-0.022em] max-w-4xl text-white">
            {episode.title}
          </h1>

          {parsed.intro && (
            <p className="text-[18px] sm:text-[20px] leading-relaxed text-white/70 mt-6 max-w-3xl whitespace-pre-line">
              {parsed.intro}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-4 mt-8">
            {episode.youtube && (
              <a
                href={episode.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 bg-brand-red text-white font-heading text-cta font-bold px-6 py-3.5 rounded-smooth hover:opacity-90 transition-opacity"
              >
                <FaYoutube className="text-lg" aria-hidden="true" />
                Watch on YouTube
              </a>
            )}
            <Link to="/podcast" className="inline-flex items-center gap-2 text-white/70 font-heading font-semibold text-cta hover:text-white transition-colors">
              <FaArrowLeft className="text-xs" aria-hidden="true" />
              All episodes
            </Link>
          </div>
        </ContentColumn>
      </section>

      {/* ── Video (lifted over the hero) ── */}
      {embedUrl && (
        <ContentColumn anchor="podcast-video" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 md:-mt-24 relative z-10">
          <div className="relative w-full overflow-hidden rounded-2xl bg-black ring-1 ring-white/10 shadow-[0_30px_80px_-30px_rgba(9,36,75,0.65)]" style={{ aspectRatio: '16 / 9' }}>
            <iframe
              key={startAt ?? 'default'}
              src={embedUrl}
              title={episode.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
              loading="lazy"
            />
          </div>
        </ContentColumn>
      )}

      {/* ── Body ── */}
      <ContentColumn anchor="podcast-body" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-12 min-w-0">
            {parsed.takeaways.length > 0 && (
              <section>
                <h2 className="font-heading text-h3 font-bold text-brand-navy mb-5">In this episode</h2>
                <ul className="space-y-3">
                  {parsed.takeaways.map((t, i) => (
                    <li key={i} className="flex gap-3 text-brand-navy/80 text-body leading-relaxed">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-red" aria-hidden="true" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {parsed.chapters.length > 0 && (
              <section>
                <h2 className="font-heading text-h3 font-bold text-brand-navy mb-5">Chapters</h2>
                <ol className="rounded-xl border border-brand-navy/12 bg-white overflow-hidden divide-y divide-brand-navy/8">
                  {parsed.chapters.map((c) => {
                    const active = startAt === c.seconds
                    return (
                      <li key={c.seconds}>
                        <button
                          type="button"
                          onClick={() => seek(c.seconds)}
                          className={`group flex w-full items-center gap-4 px-4 py-3 text-left transition-colors ${active ? 'bg-brand-red/[0.06]' : 'hover:bg-brand-navy/[0.03]'}`}
                          aria-label={`Play from ${c.time}: ${c.label}`}
                        >
                          <span className={`inline-flex items-center gap-2 shrink-0 font-accent text-body-sm tabular-nums ${active ? 'text-brand-red' : 'text-brand-navy/55 group-hover:text-brand-red'} transition-colors`}>
                            <FaPlay className="text-[9px]" aria-hidden="true" />
                            {c.time}
                          </span>
                          <span className={`font-heading text-body-sm font-semibold leading-snug ${active ? 'text-brand-red' : 'text-brand-navy'}`}>
                            {c.label}
                          </span>
                        </button>
                      </li>
                    )
                  })}
                </ol>
                {embedUrl && <p className="mt-3 text-caption text-brand-navy/45">Tap a chapter to jump the video to that moment.</p>}
              </section>
            )}

            {scriptParagraphs.length > 0 && (
              <section>
                <h2 className="font-heading text-h3 font-bold text-brand-navy mb-5">Transcript</h2>
                <div className="space-y-4 text-body text-brand-navy/75 leading-relaxed">
                  {scriptParagraphs.map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              </section>
            )}

            {/* Fallback: no structured sections and no transcript → show the raw
                description here so nothing is lost (intro already sits in the hero). */}
            {parsed.takeaways.length === 0 && parsed.chapters.length === 0 && scriptParagraphs.length === 0 && !parsed.intro && episode.description && (
              <p className="text-body text-brand-navy/75 leading-relaxed whitespace-pre-line">{episode.description}</p>
            )}

            {/* End-of-episode email capture (owned list). These pages take the
                LinkedIn and X traffic every episode post sends and had no way
                to catch any of it: no form, no newsletter link, nothing.
                Tagged per-slug, and split from the rail below, so the two
                placements can be judged apart the way the article ones are. */}
            <InlineSubscribe source={`podcast:${episode.slug}`} pitch={PODCAST_PITCH} />
          </div>

          {/* Sidebar */}
          <aside className="space-y-6 lg:sticky lg:top-8 self-start">
            {/* Go deeper / cohort CTA */}
            <div className="rounded-2xl bg-brand-navy text-white p-6 ring-1 ring-brand-gold/25">
              <p className="font-heading text-overline uppercase tracking-[0.16em] text-brand-gold mb-3">Go deeper</p>
              <p className="font-heading text-body-sm font-bold text-white leading-snug mb-2">{goDeeper.title}</p>
              <p className="text-body-sm text-white/75 leading-relaxed">
                {goDeeper.blurb}
              </p>
              {formatSessionDate(goDeeper.startsAt) ? (
                <p className="mt-3 text-caption font-heading font-semibold text-brand-gold">
                  {formatSessionDate(goDeeper.startsAt)}
                  {formatDuration(goDeeper.durationMins) ? ` · ${formatDuration(goDeeper.durationMins)}` : ''}
                </p>
              ) : null}
              <a
                href={goDeeper.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 rounded-smooth bg-white text-brand-navy font-heading text-body-sm font-bold px-4 py-2.5 hover:opacity-90 transition-opacity"
              >
                {goDeeper.kind === 'lesson' ? 'Save your free seat ↗' : 'View the cohort ↗'}
              </a>
            </div>

            {/* Episode info */}
            <div className="rounded-2xl border border-brand-navy/12 bg-white p-6">
              <h3 className="font-heading text-caption font-bold uppercase tracking-[0.16em] text-brand-navy/45 mb-4">Episode</h3>
              <dl className="space-y-3 text-body-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-brand-navy/50">Number</dt>
                  <dd className="font-semibold text-brand-navy font-accent tabular-nums">#{episode.episode}</dd>
                </div>
                {publishedDate && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-brand-navy/50">Published</dt>
                    <dd className="font-semibold text-brand-navy text-right">{publishedDate}</dd>
                  </div>
                )}
                {runtimeMin && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-brand-navy/50">Runtime</dt>
                    <dd className="font-semibold text-brand-navy font-accent tabular-nums">~{runtimeMin} min</dd>
                  </div>
                )}
              </dl>
              {episode.tags?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-brand-navy/10 flex flex-wrap gap-1.5">
                  {episode.tags.map((tag: string) => (
                    <span key={tag} className="inline-block rounded-full bg-brand-navy/[0.06] text-brand-navy/70 text-caption font-heading font-semibold px-2.5 py-0.5">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Links */}
            {otherLinks.length > 0 && (
              <div className="rounded-2xl border border-brand-navy/12 bg-white p-6">
                <h3 className="font-heading text-caption font-bold uppercase tracking-[0.16em] text-brand-navy/45 mb-4">Links</h3>
                <ul className="space-y-2.5">
                  {otherLinks.map((l) => (
                    <li key={l.url}>
                      <a href={l.url} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-1.5 text-body-sm font-semibold text-brand-navy hover:text-brand-red transition-colors">
                        {l.label}
                        <FaArrowRight className="text-[10px] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" aria-hidden="true" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Newsletter, last in the sidebar on purpose. The navy "Go deeper"
                card at the top is the paid ask; putting a free one directly
                under it would have two CTAs competing in the same eye-path.
                Paid first, episode facts, then the free ask. */}
            <div className="rounded-2xl border border-brand-navy/12 bg-white p-6">
              <InlineSubscribe source={`podcast-rail:${episode.slug}`} variant="rail" pitch={PODCAST_PITCH} />
            </div>
          </aside>
        </div>

        {/* ── Prev / next ── */}
        {(newerEpisode || olderEpisode) && (
          <nav aria-label="Episode navigation" className="mt-14 pt-8 border-t border-brand-navy/12 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {newerEpisode ? (
              <Link to={`/podcast/${newerEpisode.slug}`} className="group flex flex-col gap-1.5 rounded-xl border border-brand-navy/12 bg-white p-5 hover:border-brand-red/60 hover:shadow-[0_12px_30px_-16px_rgba(9,36,75,0.35)] transition-all">
                <span className="inline-flex items-center gap-2 font-heading text-caption uppercase tracking-[0.16em] text-brand-navy/45">
                  <FaArrowLeft className="text-[10px]" aria-hidden="true" /> Newer episode
                </span>
                <span className="font-heading font-bold text-brand-navy group-hover:text-brand-red transition-colors line-clamp-2 leading-snug">
                  {newerEpisode.title}
                </span>
              </Link>
            ) : <div className="hidden sm:block" />}

            {olderEpisode ? (
              <Link to={`/podcast/${olderEpisode.slug}`} className="group flex flex-col gap-1.5 rounded-xl border border-brand-navy/12 bg-white p-5 text-right hover:border-brand-red/60 hover:shadow-[0_12px_30px_-16px_rgba(9,36,75,0.35)] transition-all">
                <span className="inline-flex items-center justify-end gap-2 font-heading text-caption uppercase tracking-[0.16em] text-brand-navy/45">
                  Older episode <FaArrowRight className="text-[10px]" aria-hidden="true" />
                </span>
                <span className="font-heading font-bold text-brand-navy group-hover:text-brand-red transition-colors line-clamp-2 leading-snug">
                  {olderEpisode.title}
                </span>
              </Link>
            ) : <div className="hidden sm:block" />}
          </nav>
        )}
      </ContentColumn>
    </div>
  )
}

export default PodcastEpisodePage
